import React, { useState } from 'react';
import { observer } from 'mobx-react-lite';
import globalSettingsBranches from '../../../../../stores/global-settings-branches';
import globalSettingsStores from '../../../../../stores/settings-stores';
import { getHebrewYear } from '../../../../../utils/hebrew-date-utils';
import { Calendar } from 'primereact/calendar';
import { Button } from 'primereact/button';
import { FilterExpanderWithListOfCheckboxes } from '../../../../../components/CustomExpander/componenets/filter-expander-with-list-of-checkboxes';
import { CustomExpander } from '../../../../../components/CustomExpander/CustomExpander';
import { LecturesSideBarFilterDatamodel } from './lectures-side-bar-filter-datamodel';
import { LectureStatusType } from '../../../../../api/types/programs/course/lectures/lecture-status.type';

interface YearOption {
  year: string;
  code: string;
  value: number;
}

interface StatusOption {
  status: LectureStatusType;
  name: string;
  severity: 'success' | 'warning' | 'danger' | 'info';
}

export interface LecturesSideBarFilterProps {
  onSearch: (filters: LecturesSideBarFilterDatamodel) => any;
}

export const LecturesSideBarFilter: React.FC<LecturesSideBarFilterProps> =
  observer(({ onSearch }) => {
    const [selectedBranches, setSelectedBranches] = useState<number[]>([]);
    const [selectedPrograms, setSelectedPrograms] = useState<number[]>([]);
    const [selectedYears, setSelectedYears] = useState<number[]>([]);
    const [selectedStatuses, setSelectedStatuses] = useState<
      LectureStatusType[]
    >([]);
    const [lectureDateRange, setLectureDateRange] = useState<
      [Date | null, Date | null]
    >([null, null]);

    // Generate years array
    const years: YearOption[] = [];
    for (let year = 1980; year <= 2030; year++) {
      years.push({
        year: `${getHebrewYear(year)} (${year} - ${year + 1})`,
        code: 'year',
        value: year,
      });
    }

    const statuses: StatusOption[] = [
      {
        status: LectureStatusType.NOT_STARTED,
        name: 'טרם התחיל',
        severity: 'info',
      },
      { status: LectureStatusType.ACTIVE, name: 'פעיל', severity: 'success' },
      {
        status: LectureStatusType.PENDING_APPROVAL,
        name: 'ממתין לאישור',
        severity: 'warning',
      },
      {
        status: LectureStatusType.COMPLETED_SUCCESSFULLY,
        name: 'הושלם בהצלחה',
        severity: 'success',
      },
      {
        status: LectureStatusType.NOT_EXECUTED,
        name: 'לא בוצע',
        severity: 'danger',
      },
    ];

    const handleBranchToggle = (branchId: number) => {
      setSelectedBranches((prev) =>
        prev.includes(branchId)
          ? prev.filter((b) => b !== branchId)
          : [...prev, branchId],
      );
    };

    const handleProgramToggle = (programId: number) => {
      setSelectedPrograms((prev) =>
        prev.includes(programId)
          ? prev.filter((p) => p !== programId)
          : [...prev, programId],
      );
    };

    const handleYearToggle = (yearStr: string) => {
      const year = parseInt(yearStr);
      setSelectedYears((prev) =>
        prev.includes(year) ? prev.filter((y) => y !== year) : [...prev, year],
      );
    };

    const handleStatusToggle = (status: LectureStatusType) => {
      setSelectedStatuses((prev) =>
        prev.includes(status)
          ? prev.filter((s) => s !== status)
          : [...prev, status],
      );
    };

    const handleSearch = () => {
      const filters: LecturesSideBarFilterDatamodel = {
        branchesIds: selectedBranches,
        programIds: selectedPrograms,
        startYears: selectedYears,
        statuses: selectedStatuses,
        lectureDateStart: lectureDateRange[0],
        lectureDateEnd: lectureDateRange[1],
      };
      onSearch(filters);
    };

    const styles = {
      container: {
        height: '100%',
        background: 'white',
        borderRadius: '8px',
        padding: '16px',
        boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)',
        display: 'flex',
        flexDirection: 'column' as const,
      },
      header: {
        fontSize: '1.2rem',
        fontWeight: 'bold',
        marginBottom: '16px',
        color: '#333',
      },
      content: {
        flex: 1,
        overflowY: 'auto' as const,
        display: 'flex',
        flexDirection: 'column' as const,
        gap: '8px',
        paddingRight: '4px',
      },
      dateRangeContainer: {
        display: 'flex',
        flexDirection: 'column' as const,
        gap: '8px',
        padding: '8px',
      },
      calendarWrapper: {
        display: 'flex',
        flexDirection: 'column' as const,
        gap: '8px',
      },
      calendarLabel: {
        fontSize: '0.9rem',
        color: '#666',
      },
      buttonContainer: {
        marginTop: '16px',
        padding: '8px',
        borderTop: '1px solid #e9ecef',
      },
      searchButton: {
        width: '100%',
        backgroundColor: '#2196F3',
        color: 'white',
        border: 'none',
        borderRadius: '8px',
        padding: '12px',
        fontSize: '1rem',
        fontWeight: 'bold',
        cursor: 'pointer',
        transition: 'background-color 0.2s',
      },
    };

    return (
      <div style={styles.container}>
        <div style={styles.header}>סינונים</div>
        <div style={styles.content}>
          <FilterExpanderWithListOfCheckboxes
            title="שלוחות"
            items={globalSettingsBranches.branches}
            selectedItems={selectedBranches}
            onItemToggle={handleBranchToggle}
            getItemLabel={(branch) => branch.branchName}
            getItemKey={(branch) => branch.id}
            defaultExpanded={true}
            searchPlaceholder="חפש שלוחה..."
          />

          <FilterExpanderWithListOfCheckboxes
            title="תכניות לימודים"
            items={globalSettingsStores.abstractProgram}
            selectedItems={selectedPrograms}
            onItemToggle={handleProgramToggle}
            getItemLabel={(program) => program.programName}
            getItemKey={(program) => program.id}
            searchPlaceholder="חפש תכנית..."
          />

          <FilterExpanderWithListOfCheckboxes
            title="שנת התחלה"
            items={years}
            selectedItems={selectedYears.map(String)}
            onItemToggle={handleYearToggle}
            getItemLabel={(yearOption) => yearOption.year}
            getItemKey={(yearOption) => yearOption.value.toString()}
            searchPlaceholder="חפש שנה..."
          />

          <CustomExpander>
            <div>טווח תאריכי מפגש</div>
            <div style={styles.dateRangeContainer}>
              <div style={styles.calendarWrapper}>
                <span style={styles.calendarLabel}>מתאריך:</span>
                <Calendar
                  value={lectureDateRange[0]}
                  onChange={(e) =>
                    setLectureDateRange([e.value as Date, lectureDateRange[1]])
                  }
                  dateFormat="dd/mm/yy"
                  showIcon
                  iconPos="right"
                  style={{
                    height: '36px',
                    width: '100%',
                    direction: 'ltr',
                  }}
                  inputStyle={{ direction: 'rtl' }}
                />
              </div>
              <div style={styles.calendarWrapper}>
                <span style={styles.calendarLabel}>עד תאריך:</span>
                <Calendar
                  value={lectureDateRange[1]}
                  onChange={(e) =>
                    setLectureDateRange([lectureDateRange[0], e.value as Date])
                  }
                  dateFormat="dd/mm/yy"
                  showIcon
                  iconPos="right"
                  style={{
                    height: '36px',
                    width: '100%',
                    direction: 'ltr',
                  }}
                  inputStyle={{ direction: 'rtl' }}
                  minDate={lectureDateRange[0] || undefined}
                />
              </div>
            </div>
          </CustomExpander>

          <FilterExpanderWithListOfCheckboxes
            title="סטטוס"
            items={statuses}
            selectedItems={selectedStatuses}
            onItemToggle={handleStatusToggle}
            getItemLabel={(status) => status.name}
            getItemKey={(status) => status.status}
            searchPlaceholder="חפש סטטוס..."
          />
        </div>
        <div style={styles.buttonContainer}>
          <Button
            label="חפש"
            icon="pi pi-search"
            style={styles.searchButton}
            onClick={handleSearch}
          />
        </div>
      </div>
    );
  });
